import { notification } from 'antd';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { ReactElement } from 'react';

import { CheckCircleOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import { LOCAL_STORAGE_KEY } from '@common/constants';

import './notifier-item.scss';

export enum NotifierType {
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
}

export interface SimpleMessageProps {
    description: string;
    message?: string | null;
}

export interface SavedNotification {
    estimateNumber: string;
    notifierType: NotifierType;
    title: string;
    description: string;
    message?: string;
    duration?: number;
}

export type Notifications = SavedNotification[];

export const getSessionData = (key: LOCAL_STORAGE_KEY): Notifications | undefined => {
    const data = sessionStorage.getItem(key);
    if (!data) {
        return;
    }
    return JSON.parse(data);
};

export const putSessionData = (newData: unknown, key = LOCAL_STORAGE_KEY.NOTIFICATIONS): void => {
    sessionStorage.setItem(key, JSON.stringify(newData));
};

export const addNotifications = (messages: Notifications): void => {
    const data = getSessionData(LOCAL_STORAGE_KEY.NOTIFICATIONS);
    const current: Notifications = data ?? [];
    current.push(...messages);
    putSessionData(current);
};

export const getNotificationsFor = (estimateNumber: string): Notifications => {
    const data = getSessionData(LOCAL_STORAGE_KEY.NOTIFICATIONS);
    const current: Notifications = data ?? [];
    // remove any of my messages from the saved list
    putSessionData(current.filter((item) => item.estimateNumber !== estimateNumber));

    const messages = current.filter((item) => item.estimateNumber === estimateNumber);
    messages.forEach((item) => {
        showNotification({
            type: item.notifierType,
            title: item.title,
            description: item.description,
            message: item.message,
            duration: item.duration,
        });
    });
    return messages;
};

export const SimpleMessage = ({ description, message }: SimpleMessageProps): ReactElement => {
    return (
        <div className="description-container">
            <div className="Notification Notification__Description">{description}</div>
            {message && <div className="Notification__Message">{parse(message)}</div>}
        </div>
    );
};

export interface StatusErrorProps {
    description?: string | null;
    message: string | null;
    children?: React.ReactNode | undefined;
}

export const StatusError = ({ description, message, children }: StatusErrorProps): ReactElement => {
    return (
        <div className="description-container">
            {description && <div style={{ marginBottom: '10px' }}>{description}</div>}
            {message && <div>{message}</div>}
            {children}
        </div>
    );
};

interface ShowNotificationParams {
    type: NotifierType;
    title: string;
    description: string | React.ReactElement;
    message?: string | undefined;
    duration?: number;
    key?: string;
    onClose?: () => void;
}

/**
 *
 * @param type what type of notification
 * @param title the title
 * @param description if this is a jsx element, then the message is ignored
 * @param message simple text message
 * @param duration how long to remain before auto-closing
 */
export const showNotification = ({
    type,
    title,
    description,
    message = '',
    duration = 5,
    key,
    onClose,
}: ShowNotificationParams): { close: () => void } => {
    const notifierKey = key ?? `notification-${Date.now()}`;

    const icons = {
        success: <CheckCircleOutlined />,
        warning: <WarningOutlined />,
        error: <StopOutlined />,
    };

    const classes = {
        success: 'success-container',
        warning: 'warning-container',
        error: 'error-container',
    };

    //
    const desc =
        typeof description === 'string' ? <SimpleMessage description={description} message={message} /> : description;

    notification.open({
        key: notifierKey,
        onClose,
        message: title,
        description: desc,
        icon: icons[type],
        className: classNames(classes[type], 'Notification'),
        duration: duration,
    });

    return {
        close: () => {
            notification.close(notifierKey);
        },
    };
};
